import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { TablePagination } from "@material-ui/core";
import { Link } from "react-router-dom";

import TablePaginationActions from "../../component/table/TablePagination";

const statusClass = {
  pending: "badge bg-warning text-dark",
  in_progress: "badge bg-info text-dark",
  retry: "badge bg-warning text-dark",
  face_searching: "badge bg-info text-dark",
  review_pending: "badge bg-warning text-dark",
  duplicate_detected: "badge bg-danger",
  enrollment_failed: "badge bg-danger",
  locked: "badge bg-dark",
  expired: "badge bg-secondary",
  superseded: "badge bg-secondary",
};

const userName = (user) => user?.username || user?.name || "Deleted user";
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");
const formatNumber = (value) => (typeof value === "number" ? value.toFixed(2) : "—");

const LivenessAttempts = () => {
  const [attempts, setAttempts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [windowDays, setWindowDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAttempts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get("liveness/admin/liveness-attempts", {
        params: { page: pagination.page, limit: pagination.limit },
      });
      const data = response.data?.data || {};
      setAttempts(data.attempts || []);
      setWindowDays(data.windowDays || 60);
      setPagination((current) => ({ ...current, ...(data.pagination || {}) }));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to load liveness attempts.");
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last"><h3 className="mb-2 text-white">Liveness Attempts</h3></div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb"><li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li><li className="breadcrumb-item active">Liveness Attempts</li></ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header pb-0">
          <div className="row align-items-center my-2">
            <div className="col-12 col-md-8"><p className="mb-0 text-muted">The latest attempt for each user from the last {windowDays} days. Users are shown only when that attempt is not verified.</p></div>
            <div className="col-12 col-md-4 mt-3 mt-md-0 text-md-end"><button type="button" className="btn btn-outline-danger" onClick={loadAttempts} disabled={loading}>Refresh</button></div>
          </div>
        </div>
        <div className="card-body card-overflow">
          {error && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={loadAttempts}>Retry</button></div>}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead className="text-center"><tr><th>Date</th><th>User Name</th><th>Unique ID</th><th>IP</th><th>Threshold</th><th>Confidence</th><th>Status</th></tr></thead>
              <tbody className="text-center">
                {loading ? <tr><td colSpan="7" className="py-5"><span className="spinner-border spinner-border-sm text-danger me-2" />Loading liveness attempts…</td></tr>
                  : attempts.length === 0 ? <tr><td colSpan="7" className="py-5">No latest unverified liveness attempts found in the last {windowDays} days.</td></tr>
                    : attempts.map((attempt) => <tr key={attempt._id}>
                      <td><small>{formatDateTime(attempt.createdAt)}</small></td>
                      <td>{userName(attempt.user)}</td>
                      <td>{attempt.user?.uniqueId ?? "—"}</td>
                      <td><small>{attempt.clientIp || "—"}</small></td>
                      <td>{formatNumber(attempt.threshold)}</td>
                      <td>{formatNumber(attempt.confidence)}</td>
                      <td><span className={statusClass[attempt.status] || "badge bg-secondary"}>{attempt.status?.replace(/_/g, " ") || "—"}</span></td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <TablePagination component="div" count={pagination.total || 0} page={Math.max(0, (pagination.page || 1) - 1)} rowsPerPage={pagination.limit || 20}
            rowsPerPageOptions={[10, 20, 50, 100]} onChangePage={(_event, page) => setPagination((current) => ({ ...current, page: page + 1 }))}
            onChangeRowsPerPage={(event) => setPagination((current) => ({ ...current, page: 1, limit: Number(event.target.value) }))} ActionsComponent={TablePaginationActions} />
        </div>
      </div>
    </>
  );
};

export default LivenessAttempts;
